import React from "react";
import { useNavigate } from "react-router-dom";
import { FaPencilAlt } from "react-icons/fa";
import { Check, X } from "lucide-react";

const PRICE_FIELDS = [
  {
    label: "Selling",
    apiKey: "price",
    keys: ["sellingPrice"],
  },
  {
    label: "Home Delivery",
    apiKey: "home_delivery_price",
    keys: ["homeDeliveryPrice", "homePrice"],
  },
  {
    label: "Takeaway",
    apiKey: "takeaway_price",
    keys: ["takewayPrice", "takeawayPrice"],
  },
  {
    label: "Dining",
    apiKey: "dining_price",
    keys: ["diningPrice"],
  },
  {
    label: "Room Order",
    apiKey: "room_order_price",
    keys: ["roomOrderPrice", "roomPrice"],
  },
  {
    label: "Car Order",
    apiKey: "car_order_price",
    keys: ["carOrderPrice", "carPrice"],
  },
];

const TAX_OPTIONS = [0, 5, 12, 18, 28];

const formatDateForInput = (value) => {
  if (!value) return "";

  const match = String(value).match(/^(\d{4})[/-](\d{2})[/-](\d{2})/);

  if (!match) return "";

  const [, year, month, day] = match;

  return `${year}-${month}-${day}`;
};

const getValue = (obj, keys = []) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }

  return "";
};

const fmt = (v) => (v === undefined || v === null || v === "" ? "—" : v);

const getPriceRowState = (row = {}, index = 0, prev = {}) => ({
  selectedPriceIndex: index,

  id: row.id || "",
  sizeId: row.size_id || "",
  sizeName: row.size || "",
  size: row.size || "",

  sellingPrice: row.price ?? "",

  homeDeliveryPrice: row.home_delivery_price ?? "",
  homePrice: row.home_delivery_price ?? "",

  takewayPrice: row.takeaway_price ?? "",
  takeawayPrice: row.takeaway_price ?? "",

  diningPrice: row.dining_price ?? "",

  roomOrderPrice: row.room_order_price ?? "",
  roomPrice: row.room_order_price ?? "",

  carOrderPrice: row.car_order_price ?? "",
  carPrice: row.car_order_price ?? "",

  taxPercentage: row.item_tax ?? "",
  tax: row.item_tax ?? "",

  variantId:
    row.variantId !== undefined && row.variantId !== ""
      ? row.variantId
      : row.size_id || prev.variantId,

  variantName:
    row.variantName !== undefined && row.variantName !== ""
      ? row.variantName
      : row.size || prev.variantName || "",
});

export default function CatalogueItemModal({
  viewModalItem,
  setViewModalItem,

  viewTab,
  setViewTab,

  isEditing,
  setIsEditing,

  editableDetails,
  setEditableDetails,

  handleEditClick,
  handleSaveFromViewMode,

  subCategories,
  racks,
  variants,

  availabilityData,
  setAvailabilityData,
  submitAvailability,

  fetchPrinters,
  printerLoading,
  printers,
  setSelectedPrinter,
  savePrinterToItem,
  setAddPrinterOpen,
}) {
  const navigate = useNavigate();

  if (!viewModalItem) return null;

  const closeModal = () => {
    setViewModalItem(null);
    setIsEditing(false);
  };

  const applyPriceRow = (selected, index) => {
    if (!selected) return;

    setEditableDetails((prev) => ({
      ...prev,
      ...getPriceRowState(selected, index, prev),
    }));
  };

  const renderPriceLines = (data) => (
    <div className="space-y-2">
      {PRICE_FIELDS.map((field) => (
        <div key={field.label} className="flex justify-between gap-3 text-sm">
          <span className="text-gray-600">{field.label}</span>
          <span className="font-medium text-gray-900 tabular-nums">
            ₹{fmt(getValue(data, field.keys))}
          </span>
        </div>
      ))}
    </div>
  );

  const renderVariantPriceLines = (row) => (
    <div className="space-y-1.5 text-sm">
      {PRICE_FIELDS.map((field) => (
        <div key={field.label} className="flex justify-between gap-2">
          <span className="text-gray-600">{field.label}</span>
          <span className="font-medium tabular-nums">
            ₹{fmt(row?.[field.apiKey])}
          </span>
        </div>
      ))}
    </div>
  );

  const renderViewMode = () => {
    const d = editableDetails || {};
    const taxPct = d.taxPercentage ?? d.tax ?? 0;
    const variantLabel = d.variantName || d.size || "—";
    const variantIdDisplay =
      d.variantId !== undefined && d.variantId !== ""
        ? String(d.variantId)
        : "—";

    return (
      <>
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="aspect-square w-full overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
              {d.photo ? (
                <img
                  src={d.photo}
                  alt={d.item_name || ""}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-gray-500">
                  No image
                </div>
              )}
            </div>

            <div className="mt-3 text-xs font-medium text-gray-500">
              Description
            </div>

            <div className="mt-1 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm leading-relaxed text-gray-800">
              {d.item_description || "—"}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 text-sm font-semibold text-gray-900">
              Basic Details
            </div>

            <dl className="space-y-3">
              {[
                ["Name", d.item_name],
                ["Sub-category", d.category],
                ["Rack Number", d.rackNo],
                ["Expiry", d.expiryDate],
              ].map(([label, val]) => (
                <div key={label}>
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="mt-0.5 text-sm font-medium text-gray-900">
                    {fmt(val)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 text-sm font-semibold text-gray-900">
              Current Editable Row
            </div>

            <div className="mb-3 flex flex-wrap gap-2">
              <span className="rounded-md bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                {variantLabel}
              </span>

              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                Tax: {taxPct}%
              </span>
            </div>

            {renderPriceLines(d)}

            <div className="my-3 border-t border-gray-100" />

            <div className="text-xs text-gray-500">Variant</div>
            <div className="mt-0.5 text-sm font-medium text-gray-900">
              {variantLabel} (ID: {variantIdDisplay})
            </div>
          </div>
        </div>

        <VariantRowsGrid
          rows={viewModalItem?.prices || []}
          selectedPriceIndex={d.selectedPriceIndex}
          onSelect={applyPriceRow}
          renderVariantPriceLines={renderVariantPriceLines}
          buttonMode="select"
        />

        <ModalFooter
          closeModal={closeModal}
          navigate={navigate}
          viewModalItem={viewModalItem}
        />
      </>
    );
  };

  const renderEditMode = () => {
    const d = editableDetails || {};
    const priceRows = viewModalItem?.prices || [];
    const rowCount = priceRows.length;
    const rowNum = (d.selectedPriceIndex ?? 0) + 1;

    const variantLabel = d.variantName || d.sizeName || d.size || "—";
    const variantIdDisplay =
      d.variantId !== undefined && d.variantId !== ""
        ? String(d.variantId)
        : "—";

    const updateMultiKeys = (keys, value) => {
      setEditableDetails((prev) => {
        const next = { ...prev };

        keys.forEach((key) => {
          next[key] = value;
        });

        return next;
      });
    };

    return (
      <>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="w-full shrink-0 lg:w-[240px]">
            <div className="aspect-square w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              {d.photo ? (
                <img
                  src={d.photo}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-gray-500">
                  No image
                </div>
              )}
            </div>

            <p className="mt-4 text-sm font-medium text-gray-700">
              Update Image
            </p>

            <input
              type="file"
              accept="image/*"
              className="mt-2 w-full cursor-pointer text-sm file:mr-3 file:rounded-md file:border-0 file:bg-gray-200 file:px-3 file:py-1.5 file:text-sm"
            />
          </div>

          <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Edit Item Details
              </h2>

              <span className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600">
                Row {rowNum} of {rowCount || 1}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Item Name"
                value={d.item_name}
                onChange={(v) =>
                  setEditableDetails((prev) => ({ ...prev, item_name: v }))
                }
              />
              <div>
                <label className="text-xs text-gray-600">Description</label>
                <textarea
                  rows={3}
                  value={d.item_description || ""}
                  onChange={(e) =>
                    setEditableDetails((prev) => ({
                      ...prev,
                      item_description: e.target.value,
                    }))
                  }
                  className="mt-1 w-full resize-y rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Select
                label="Sub-category"
                value={d.categoryId || ""}
                onChange={(val) => {
                  const selected = (subCategories || []).find(
                    (sc) => String(sc.id) === String(val),
                  );

                  setEditableDetails((prev) => ({
                    ...prev,
                    categoryId: val,
                    category: selected?.category || prev.category,
                  }));
                }}
              >
                <option value="">Select sub-category</option>
                {(subCategories || []).map((sc) => (
                  <option key={sc.id} value={sc.id}>
                    {sc.category}
                  </option>
                ))}
              </Select>
              <Select
                label="Rack Number"
                value={d.rackNo || ""}
                onChange={(val) => {
                  const selected = (racks || []).find(
                    (r) => String(r.rackNumber || r.id) === String(val),
                  );

                  setEditableDetails((prev) => ({
                    ...prev,
                    rackNo: selected?.rackNumber || "",
                  }));
                }}
              >
                <option value="">Select rack</option>
                {(racks || []).map((r, i) => (
                  <option key={r.id || i} value={r.rackNumber || r.id}>
                    {`Rack ${r.rackNumber} — ${r.category || ""}`}
                  </option>
                ))}
              </Select>
              <Input
                type="date"
                label="Expiry Date"
                value={formatDateForInput(d.expiryDate)}
                onChange={(v) =>
                  setEditableDetails((prev) => ({
                    ...prev,
                    expiryDate: v,
                  }))
                }
              />{" "}
              <Input
                type="date"
                label="Expiry Date"
                value={d.expiryDate}
                onChange={(v) =>
                  setEditableDetails((prev) => ({ ...prev, expiryDate: v }))
                }
              />
              <Select
                label="Variant"
                value={d.variantId || ""}
                onChange={(val) => {
                  const selected = (variants || []).find(
                    (v) => String(v.pk_variant_id) === String(val),
                  );

                  setEditableDetails((prev) => ({
                    ...prev,
                    variantId: val,
                    variantName: selected?.variant_title || "",
                  }));
                }}
              >
                <option value="">Select variant</option>
                {(variants || []).map((v) => (
                  <option key={v.pk_variant_id} value={v.pk_variant_id}>
                    {v.variant_title}
                  </option>
                ))}
              </Select>
              <div className="md:col-span-2">
                <Select
                  label="Selected Size / Price Row"
                  value={
                    d.selectedPriceIndex != null
                      ? String(d.selectedPriceIndex)
                      : ""
                  }
                  onChange={(val) => {
                    const index = Number(val);
                    const selected = viewModalItem?.prices?.[index];
                    applyPriceRow(selected, index);
                  }}
                >
                  <option value="">Select size / row</option>
                  {priceRows.map((p, i) => (
                    <option key={i} value={i}>
                      {`${i + 1}. ${p.size || "no size"} — ₹${p.price ?? "—"}`}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="mt-8 border-t border-gray-100 pt-6">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Edit Active Size / Variant Prices
                  </h3>

                  <p className="mt-1 max-w-xl text-xs text-gray-500">
                    Each size/variant row can be opened separately and edited
                    clearly.
                  </p>
                </div>

                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                  {variantLabel}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Size Name"
                  value={d.sizeName}
                  onChange={(v) => updateMultiKeys(["sizeName"], v)}
                />

                <Input
                  label="Size ID"
                  value={d.sizeId}
                  onChange={(v) => updateMultiKeys(["sizeId"], v)}
                />

                <Select
                  label="Tax Percentage"
                  value={
                    d.tax !== undefined && d.tax !== "" ? String(d.tax) : ""
                  }
                  onChange={(val) =>
                    updateMultiKeys(["tax", "taxPercentage"], val)
                  }
                >
                  <option value="">Select Tax</option>
                  {TAX_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}%
                    </option>
                  ))}
                </Select>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {PRICE_FIELDS.map((field) => (
                  <Input
                    key={field.label}
                    label={`${field.label} Price`}
                    value={getValue(d, field.keys)}
                    onChange={(v) => updateMultiKeys(field.keys, v)}
                  />
                ))}
              </div>

              <div className="mt-4">
                <label className="text-xs text-gray-600">Variant Info</label>
                <input
                  readOnly
                  className="mt-1 w-full cursor-not-allowed rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                  value={
                    variantLabel !== "—"
                      ? `${variantLabel} (ID: ${variantIdDisplay})`
                      : "—"
                  }
                />
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                  onClick={handleSaveFromViewMode}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>

        <VariantRowsGrid
          rows={priceRows}
          selectedPriceIndex={d.selectedPriceIndex}
          onSelect={applyPriceRow}
          renderVariantPriceLines={renderVariantPriceLines}
          buttonMode="edit"
        />

        <ModalFooter
          closeModal={closeModal}
          navigate={navigate}
          viewModalItem={viewModalItem}
        />
      </>
    );
  };

  return (
    <Modal
      wide
      subtitle={`Item ID: ${viewModalItem.item_id}`}
      onClose={closeModal}
      title={viewModalItem.item_name}
      headerRight={
        !isEditing ? (
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-50"
            onClick={handleEditClick}
          >
            <FaPencilAlt className="text-base" />
            Edit
          </button>
        ) : (
          <button
            type="button"
            className="text-sm font-semibold text-red-500 hover:underline"
            onClick={() => setIsEditing(false)}
          >
            Cancel Edit
          </button>
        )
      }
    >
      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {["details", "availability", "printer"].map((t) => {
          const label =
            t === "details"
              ? "Details"
              : t === "availability"
                ? "Availability"
                : "Printer";

          return (
            <button
              type="button"
              key={t}
              className={`relative -mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                viewTab === t
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
              onClick={() => {
                setViewTab(t);

                if (t === "printer") {
                  fetchPrinters();
                }
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {viewTab === "details" &&
        (!isEditing ? renderViewMode() : renderEditMode())}

      {viewTab === "availability" && (
        <div className="space-y-2">
          <ToggleRow
            label="Home Delivery"
            checked={!!availabilityData[viewModalItem.item_id]?.homeDelivery}
            onChange={() =>
              setAvailabilityData((prev) => ({
                ...prev,
                [viewModalItem.item_id]: {
                  ...prev[viewModalItem.item_id],
                  homeDelivery: !prev[viewModalItem.item_id]?.homeDelivery,
                },
              }))
            }
          />

          <ToggleRow
            label="Takeaway"
            checked={!!availabilityData[viewModalItem.item_id]?.takeaway}
            onChange={() =>
              setAvailabilityData((prev) => ({
                ...prev,
                [viewModalItem.item_id]: {
                  ...prev[viewModalItem.item_id],
                  takeaway: !prev[viewModalItem.item_id]?.takeaway,
                },
              }))
            }
          />

          <ToggleRow
            label="Dine-in"
            checked={!!availabilityData[viewModalItem.item_id]?.dineIn}
            onChange={() =>
              setAvailabilityData((prev) => ({
                ...prev,
                [viewModalItem.item_id]: {
                  ...prev[viewModalItem.item_id],
                  dineIn: !prev[viewModalItem.item_id]?.dineIn,
                },
              }))
            }
          />

          <div className="pt-2 text-right">
            <button
              className="bg-[#01C37D] text-white px-3 py-1.5 rounded-lg text-sm"
              onClick={() => submitAvailability(viewModalItem.item_id)}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {viewTab === "printer" && (
        <>
          <p className="mb-2 text-sm text-gray-600">
            Assign a printer to this item.
          </p>

          {printerLoading ? (
            <div className="py-6 text-center text-gray-500">
              Loading printers…
            </div>
          ) : (
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              onChange={(e) =>
                setSelectedPrinter(
                  (printers || []).find(
                    (p) => p.printerCode === e.target.value,
                  ) || null,
                )
              }
            >
              <option value="">Select Printer</option>
              {(printers || []).map((p) => (
                <option key={p.printerCode} value={p.printerCode}>
                  {p.printerName} — {p.printerAlias}
                </option>
              ))}
            </select>
          )}

          <div className="mt-3 flex gap-2">
            <button
              className="bg-[#01C37D] text-white px-3 py-1.5 rounded-lg text-sm"
              onClick={savePrinterToItem}
            >
              Save Printer
            </button>

            <button
              className="border px-3 py-1.5 rounded-lg text-sm text-gray-400 cursor-not-allowed"
              disabled
            >
              Add Printer
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function VariantRowsGrid({
  rows,
  selectedPriceIndex,
  onSelect,
  renderVariantPriceLines,
  buttonMode,
}) {
  return (
    <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50/80 p-4">
      <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            All Size / Variant Rows
          </div>

          <p className="mt-0.5 text-xs text-gray-500">
            Open any row separately, review all prices quickly, and switch
            editing easily.
          </p>
        </div>

        <span className="text-sm font-medium text-gray-600">
          {(rows || []).length} rows
        </span>
      </div>

      <div
        className={`mt-4 grid grid-cols-1 gap-4 ${
          buttonMode === "select"
            ? "sm:grid-cols-2 lg:grid-cols-3"
            : "sm:grid-cols-2"
        }`}
      >
        {(rows || []).map((p, i) => {
          const isActive = selectedPriceIndex === i;
          const vName = p.variantName || p.size || `Row ${i + 1}`;
          const tax = p.item_tax ?? 0;

          const content = (
            <>
              <div className="mb-2 flex items-start justify-between gap-2">
                <span
                  className={
                    buttonMode === "select"
                      ? "text-xs text-gray-500"
                      : "text-sm font-semibold text-gray-900"
                  }
                >
                  {buttonMode === "select" ? `Variant: ${vName}` : vName}
                </span>

                <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {tax}% tax
                </span>
              </div>

              {isActive && (
                <div className="mb-3 inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                  <Check size={12} strokeWidth={3} />
                  Active
                </div>
              )}

              {renderVariantPriceLines(p)}

              {buttonMode === "edit" && (
                <div className="mt-4">
                  {isActive ? (
                    <button
                      type="button"
                      disabled
                      className="w-full cursor-default rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white opacity-90"
                    >
                      Currently Editing
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-blue-600 bg-white py-2.5 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                      onClick={() => onSelect(p, i)}
                    >
                      Edit This Row
                    </button>
                  )}
                </div>
              )}
            </>
          );

          if (buttonMode === "select") {
            return (
              <button
                type="button"
                key={i}
                className={`cursor-pointer rounded-xl border-2 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 ${
                  isActive
                    ? "border-blue-600 ring-1 ring-blue-600/20"
                    : "border-gray-200"
                }`}
                onClick={() => onSelect(p, i)}
              >
                {content}
              </button>
            );
          }

          return (
            <div
              key={i}
              className={`rounded-xl border-2 bg-white p-4 shadow-sm ${
                isActive
                  ? "border-blue-600 ring-1 ring-blue-600/15"
                  : "border-gray-200"
              }`}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModalFooter({ closeModal, navigate, viewModalItem }) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
      <button
        type="button"
        className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        onClick={closeModal}
      >
        Close
      </button>

      <button
        type="button"
        className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
        onClick={() =>
          navigate(
            `/Orders/Catalogue/edit-ingredients/${viewModalItem.item_id}`,
            {
              state: {
                name: viewModalItem.item_name,
                photo: viewModalItem.photo,
              },
            },
          )
        }
      >
        Ingredients Edit
      </button>
    </div>
  );
}

function Modal({ title, subtitle, children, onClose, headerRight, wide }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3">
      <div
        className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-5 shadow-xl ${
          wide ? "max-w-5xl" : "max-w-3xl"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-xl font-bold leading-tight text-gray-900">
              {title}
            </h2>

            {subtitle ? (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {headerRight}

            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-100" />
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2">
      <span className="text-sm">{label}</span>
      <input type="checkbox" checked={checked} onChange={onChange} />
    </label>
  );
}

const Input = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="text-xs text-gray-600">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full mt-1 border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
    />
  </div>
);

const Select = ({ label, children, value, onChange }) => (
  <div>
    <label className="text-xs text-gray-600">{label}</label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
    >
      {children}
    </select>
  </div>
);
